"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, FlaskConical, Layers, Sparkles, CircleCheck as CheckCircle2, ShieldCheck, ChartBar as BarChart3 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const features = [{
  title: "Flowability and Shear Testing",
  description: "Accurate characterization of powder behavior across hoppers, dies, and feeders."
}, {
  title: "Bulk Density and Tap Testing",
  description: "Standardized USP/EP compliance testing for volume reduction and packing properties."
}, {
  title: "Moisture and Content Uniformity",
  description: "In-line and laboratory PAT solutions for rapid blend and drying verification."
}];
export default function PowderCallout() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="powdertestingcallout" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-y border-border">
      <div className="page-container">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative overflow-hidden rounded-2xl bg-secondary/50 border border-border p-8 md:p-12 lg:p-16 shadow-xs">
          {/* Subtle Background Pattern Accent */}
          <div className="absolute -top-24 -right-24 size-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-accent/30 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left Content Column */}
            <div className="lg:col-span-7 flex flex-col items-start gap-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="border-border text-foreground font-medium px-3 py-1">
                  <Sparkles className="size-3.5 text-primary" />
                  Specialized Testing
                </Badge>
                <Badge variant="outline" className="text-muted-foreground text-xs px-2.5 py-0.5">
                  Pharma &amp; Manufacturing
                </Badge>
              </div>

              <div className="space-y-3">
                <h2 className="font-default text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  Comprehensive Powder Testing &amp; Characterization
                </h2>
                <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
                  Optimize your formulation performance, ensure regulatory compliance, and prevent powder flow disruptions with our qualified instrumentation and PAT solutions.
                </p>
              </div>

              {/* Feature Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full pt-2">
                {features.map((feature, i) => <div key={feature.title} data-index={i} className="flex items-start gap-3 rounded-lg bg-background/80 border border-border/80 p-3.5">
                    <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-default text-sm font-semibold text-foreground">
                        {feature.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                        {feature.description}
                      </p>
                    </div>
                  </div>)}

                <div data-index={features.length} className="flex items-start gap-3 rounded-lg bg-background/80 border border-border/80 p-3.5">
                  <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-default text-sm font-semibold text-foreground">
                      Full Qualification &amp; Support
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                      IQ/OQ protocols, operator training, and maintenance across Ireland &amp; UK.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-4 pt-4">
                <Button asChild size="lg" className="shadow-sm">
                  <Link to="/PowderTestingPage" className="inline-flex items-center gap-2">
                    Explore Powder Solutions
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg">
                  <Link to="/DiscussYourApplicationPage">
                    Discuss Your Application
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Media / Visual Card Column */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-md">
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_instrument.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_instrument.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_instrument.png-wvc-sizes" alt="Precision powder testing and analytical instrument" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent" />
                  
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <span className="text-xs font-medium tracking-wide uppercase text-primary-foreground/90 bg-primary/90 px-2 py-0.5 rounded-sm">
                      Application Ready
                    </span>
                    <p className="text-sm font-semibold text-white mt-1">
                      Turnkey testing systems engineered for regulated laboratories
                    </p>
                  </div>
                </div>

                <div className="p-5 flex items-center justify-between gap-4 bg-card">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-secondary text-secondary-foreground">
                      <BarChart3 className="size-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">Complete Data Integrity</p>
                      <p className="text-xs text-muted-foreground">21 CFR Part 11 compliant software</p>
                    </div>
                  </div>
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/BookADemoPage">
                      Demo
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}